/**
 * Cryptographic utilities for payment-relay iframe
 * Decrypts payment parameters sent from parent window
 * Uses AES-GCM (256-bit) for authenticated decryption
 *
 * @module crypto
 */

/**
 * Payment parameters structure after decryption
 */
import type { PaymentParams } from './types';

export type { PaymentParams };

/**
 * True when the params carry the identifiers and the signed Lightbox inputs.
 */
export function hasValidPaymentParams(params: Partial<PaymentParams>): boolean {
  return Boolean(
    params.merchantId &&
    params.terminalId &&
    params.transactionId &&
    params.secureHash &&
    params.amountTrxn &&
    params.trxDateTime &&
    params.merchantReference,
  );
}

/**
 * Get encryption key from environment variable
 * The key should be a 256-bit (32-byte) base64-encoded string
 */
function getEncryptionKey(): string {
  // Vite exposes environment variables via import.meta.env
  const key = import.meta.env.VITE_PAYMENT_ENCRYPTION_KEY;

  if (!key) {
    throw new Error(
      'VITE_PAYMENT_ENCRYPTION_KEY environment variable is not set. ' +
        'Please check your .env file and ensure the key is properly configured.',
    );
  }

  return key;
}

/**
 * Convert base64 string to Uint8Array
 */
function base64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Import the encryption key from base64-encoded string
 */
async function importKey(): Promise<CryptoKey> {
  const keyBase64 = getEncryptionKey();
  const keyBytes = base64ToBytes(keyBase64);

  return await crypto.subtle.importKey(
    'raw',
    keyBytes.buffer as ArrayBuffer,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
}

/**
 * Decrypt payment parameters using AES-GCM
 *
 * @param encryptedData - Base64-encoded encrypted data
 * @param ivBase64 - Base64-encoded initialization vector
 * @returns Decrypted payment parameters
 * @throws Error if decryption fails or data is tampered
 */
export async function decryptPaymentParams(
  encryptedData: string,
  ivBase64: string,
): Promise<PaymentParams> {
  try {
    // Convert from base64
    const encryptedBytes = base64ToBytes(encryptedData);
    const iv = base64ToBytes(ivBase64);

    // Import the encryption key
    const key = await importKey();

    // Decrypt the data
    const decryptedBytes = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv.buffer as ArrayBuffer,
      },
      key,
      encryptedBytes.buffer as ArrayBuffer,
    );

    // Convert bytes to string and parse JSON
    const decoder = new TextDecoder();
    const decryptedString = decoder.decode(decryptedBytes);
    const params = JSON.parse(decryptedString) as PaymentParams;

    // Validate the structure
    if (!hasValidPaymentParams(params)) {
      throw new Error('Invalid decrypted payload structure');
    }

    return params;
  } catch (error) {
    // AES-GCM will throw if data was tampered with
    throw new Error(
      `Failed to decrypt payment parameters: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    );
  }
}
